import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7 } from "styled-components";
import React from "react";
import { useScroll, m } from "framer-motion";
import { type Theme, type Mode, getThemeStyle } from "~/features/ui/get-theme-style";
import { debounce } from "~/features/app/utils";
import { GraphLine } from "~/features/blocks/inventory/chip-evolution-graph/graph-line";
import { type ChipEvolutionGraphItemFragment } from "~/gql/graphql";
import { ChipEvolutionGraphItem } from "~/features/blocks/inventory/chip-evolution-graph/chip-evolution-graph-item";
import { useChipEvolutionGraphBlock } from "~/features/blocks/inventory/chip-evolution-graph/context";
import { Pattern } from "~/features/app/pattern";
type Props = {
  showXAxisData: boolean;
  items: ChipEvolutionGraphItemFragment[];
};
type GraphLineArgs = {
  containerRef: React.RefObject<HTMLDivElement | null>;
  startId: string;
  endId: string;
  animate: boolean;
  index: number;
};
export type GraphViewProps<TItem> = {
  items: readonly TItem[];
  theme: Theme;
  mode: Mode;
  getItemId: (item: TItem, index: number) => string | number | null | undefined;
  getItemValue: (item: TItem) => number;
  getItemKey?: (item: TItem, index: number) => React.Key;
  renderItem: (item: TItem, index: number, options: {
    animate: boolean;
    maxValue: number;
  }) => React.ReactNode;
  renderLine: (args: GraphLineArgs) => React.ReactNode;
};
export function GraphView<TItem>({
  items,
  theme,
  mode,
  getItemId,
  getItemValue,
  getItemKey,
  renderItem,
  renderLine
}: GraphViewProps<TItem>) {
  const themeStyle = getThemeStyle({
    theme: theme === "white" ? "grey" : "white",
    mode
  });
  const containerRef = React.useRef<HTMLDivElement>(null);
  const ref = React.useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = React.useState(false);
  const [isInView, setIsInView] = React.useState(false);
  const maxValue = items.reduce((max, item) => {
    const currentParam = getItemValue(item);
    return Math.max(max, currentParam);
  }, 0);
  const {
    scrollXProgress
  } = useScroll({
    container: containerRef
  });
  React.useEffect(() => {
    function onResize() {
      if (ref.current) {
        setIsOverflowing(ref.current.scrollWidth > ref.current.clientWidth);
      }
    }
    onResize();
    const debouncedOnResize = debounce(onResize);
    window.addEventListener("resize", debouncedOnResize);
    return () => {
      window.removeEventListener("resize", debouncedOnResize);
    };
  }, []);
  return <_StyledMDiv onViewportEnter={() => setIsInView(true)} $_css={[{
    "position": "relative"
  }, themeStyle.background]}>
      <_StyledDiv>
        <Pattern pattern="dotted" />
      </_StyledDiv>
      <_StyledDiv2 ref={containerRef} className="hide-scrollbar">
        <_StyledDiv3 ref={ref} $_css2={[{
        "display": "flex",
        "minHeight": "394px",
        "@media (min-width: 1024px)": {
          "minHeight": "600px",
          "padding": "4rem"
        }
      }, themeStyle.border]}>
          {items.map((item, idx) => {
          const nextItem = items[idx + 1];
          return <React.Fragment key={getItemKey?.(item, idx) ?? idx}>
                {renderItem(item, idx, {
              animate: isInView,
              maxValue
            })}
                {nextItem && renderLine({
              animate: isInView,
              index: idx,
              containerRef: ref,
              startId: `#point-${getItemId(item, idx)}`,
              endId: `#point-${getItemId(nextItem, idx + 1)}`
            })}
              </React.Fragment>;
        })}
        </_StyledDiv3>
      </_StyledDiv2>
      {isOverflowing && <_StyledDiv4 $_css3={[{
      "width": "100%",
      "borderTopWidth": "1px",
      "paddingLeft": "1.6rem",
      "paddingRight": "1.6rem",
      "paddingTop": "3.2rem",
      "paddingBottom": "3.2rem",
      "@media (min-width: 1024px)": {
        "paddingLeft": "4rem",
        "paddingRight": "4rem",
        "paddingTop": "2.4rem",
        "paddingBottom": "2.4rem"
      }
    }, themeStyle.background, themeStyle.border]}>
          <_StyledDiv5>
            <_StyledMDiv2 style={{
          scaleX: scrollXProgress
        }} />
          </_StyledDiv5>
        </_StyledDiv4>}
    </_StyledMDiv>;
}
export function Graph(props: Props) {
  const {
    showXAxisData,
    items
  } = props;
  const {
    theme,
    mode
  } = useChipEvolutionGraphBlock();
  return <GraphView items={items} theme={theme as Theme} mode={mode as Mode} getItemId={item => item.id} getItemValue={item => item.range * item.pointsPerSecond} renderItem={(item, idx, {
    animate,
    maxValue
  }) => <ChipEvolutionGraphItem showXAxisData={showXAxisData} animate={animate} index={idx} theme={theme as Theme} mode={mode as Mode} maxValue={maxValue} {...item} />} renderLine={({
    animate,
    index,
    containerRef,
    startId,
    endId
  }) => <GraphLine animate={animate} index={index} containerRef={containerRef} startId={startId} endId={endId} />} />;
}
var _StyledMDiv = /*#__PURE__*/_styled(m.div).withConfig({
  componentId: "sc-sf-6km1qc-0"
})`${p => p.$_css}`;
var _StyledDiv = _styled2("div").withConfig({
  componentId: "sc-sf-6km1qc-1"
})({
  "position": "absolute",
  "inset": "0",
  "@media (min-width: 1024px)": {
    "inset": "4rem"
  }
});
var _StyledDiv2 = _styled3("div").withConfig({
  componentId: "sc-sf-6km1qc-2"
})({
  "position": "relative",
  "overflowX": "auto"
});
var _StyledDiv3 = _styled4("div").withConfig({
  componentId: "sc-sf-6km1qc-3"
})`${p => p.$_css2}`;
var _StyledDiv4 = _styled5("div").withConfig({
  componentId: "sc-sf-6km1qc-4"
})`${p => p.$_css3}`;
var _StyledDiv5 = _styled6("div").withConfig({
  componentId: "sc-sf-6km1qc-5"
})({
  "backgroundColor": "rgb(75 46 248 / 0.2)"
});
var _StyledMDiv2 = /*#__PURE__*/_styled7(m.div).withConfig({
  displayName: "graph___StyledMDiv2",
  componentId: "sc-xoe8ue-0"
})({
  "height": "0.4rem",
  "transformOrigin": "left",
  "--tw-bg-opacity": "1",
  "backgroundColor": "rgb(75 46 248 / var(--tw-bg-opacity))"
});